// src/hooks/useLookerLayout.ts
/**
 * The /looker layout for the payload on screen: the workspace's saved order and
 * widths merged onto the template's tiles, plus an editable draft. Only a
 * workspace scope has a layout — "all workspaces" always shows the template.
 *
 * @module hooks/useLookerLayout
 */
import { useEffect, useMemo, useState } from 'react';
import { resolveLayout, type LookerLayoutEntry } from '@/services/looker/lookerLayout';
import { fetchLookerLayout, resetLookerLayout, saveLookerLayout } from '@/services/looker/lookerLayoutService';
import type { LookerDashboardPayload } from '@/services/looker/lookerTypes';

export interface LookerLayoutState {
  layout: LookerLayoutEntry[];
  editing: boolean;
  canEdit: boolean;
  saving: boolean;
  /** An i18n key, or null. */
  error: string | null;
  start: () => void;
  change: (next: LookerLayoutEntry[]) => void;
  cancel: () => void;
  save: () => Promise<void>;
  reset: () => Promise<void>;
}

export const useLookerLayout = (payload: LookerDashboardPayload | null, mayEdit: boolean): LookerLayoutState => {
  const workspaceId = payload?.scope.kind === 'workspace' ? payload.scope.workspaceId : null;
  const templateId = payload?.template.id ?? null;
  const key = workspaceId && templateId ? `${workspaceId}:${templateId}` : '';

  const [saved, setSaved] = useState<LookerLayoutEntry[] | null>(null);
  // Until the saved layout has been read, a draft would start from the template
  // order and a save would overwrite the workspace's real layout with it.
  const [loadedKey, setLoadedKey] = useState('');
  const [draft, setDraft] = useState<LookerLayoutEntry[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A different workspace or template: forget the old layout before the new one lands.
  useEffect(() => {
    setSaved(null);
    setDraft(null);
    setError(null);
    if (!workspaceId || !templateId) return;
    let cancelled = false;
    void fetchLookerLayout(workspaceId, templateId).then((l) => {
      if (cancelled) return;
      setSaved(l);
      setLoadedKey(key);
    });
    return () => {
      cancelled = true;
    };
    // key carries both ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const tiles = useMemo(() => payload?.tiles ?? [], [payload]);
  const layout = useMemo(() => resolveLayout(tiles, draft ?? saved), [tiles, draft, saved]);

  const save = async () => {
    if (!workspaceId || !templateId || !draft) return;
    setSaving(true);
    setError(null);
    const ok = await saveLookerLayout(workspaceId, templateId, layout);
    setSaving(false);
    if (!ok) return setError('looker.layout.saveFailed');
    setSaved(layout);
    setDraft(null);
  };

  const reset = async () => {
    if (!workspaceId || !templateId) return;
    setSaving(true);
    setError(null);
    const ok = await resetLookerLayout(workspaceId, templateId);
    setSaving(false);
    if (!ok) return setError('looker.layout.saveFailed');
    setSaved(null);
    setDraft(null);
  };

  return {
    layout,
    editing: draft !== null,
    canEdit: mayEdit && key !== '' && loadedKey === key,
    saving,
    error,
    start: () => setDraft(layout),
    change: setDraft,
    cancel: () => {
      setDraft(null);
      setError(null);
    },
    save,
    reset,
  };
};
